import MomentUtils from "@date-io/moment";
import { CircularProgress } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { Button } from "@material-ui/core";
import { Theme, makeStyles } from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import { TextField } from "@material-ui/core";
import {
	KeyboardDatePicker,
	KeyboardTimePicker,
	MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { useShiftsCrewsContext } from "../../context/ShiftsCrewsContext";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { ShiftParameters } from "../../models/ShiftParameters";
import { upsertShiftParameters } from "../../repositories/ShiftsCrewsRepository";

const useStyles = makeStyles((theme: Theme) => ({
	title: {
		fontSize: 15,
	},
	modalTitle: {
		fontSize: 15,
		marginTop: theme.spacing(1),
	},
	chip: {
		margin: 2,
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
	infoLabel: {
		fontSize: 12,
		color: "#757575",
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditShift = ({ show, onHide }: Props) => {
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [
		initialShiftPerDayAndCrewsNumber,
		setInitialShiftPerDayAndCrewsNumber,
	] = useState(false);
	const classes = useStyles();
	const {
		state: { shiftTableData },
	} = useShiftsCrewsContext();

	const {
		control,
		setValue,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<ShiftParameters>({ defaultValues: INITIAL_VALUES });

	// Progress bar
	const calculatePercentage = () =>
		((crewRotation?.length as number) / maxCrewRotationLength) * 100;

	const onSubmit: SubmitHandler<ShiftParameters> = async (data) => {
		setIsSubmitLoading(true);
		const shiftParameters = await upsertShiftParameters(data);
		if (shiftParameters.ok) {
			setIsSubmitLoading(false);
			setOpen(true);
			onHide(true);
		} else {
			setError(shiftParameters.message);
			setIsSubmitLoading(false);
		}
	};

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	// const patternStart = watch('PatternStart');
	const shiftsPerDay = watch("ShiftsPerDay");
	const shiftCodes = watch("ShiftCodes");
	const numberOfCrews = watch("NumberOfCrews");
	const crewsCodes = watch("Crews");
	const rotationPattern = watch("PatternDays");
	const comments = watch("Comments");
	const crewRotation = watch("CrewRotation");
	const maxCrewRotationLength = shiftsPerDay * rotationPattern;

	useEffect(() => {
		if (show) {
			reset();
			setValue("PatternStart", shiftTableData.PatternStart);
			setValue("ShiftCodes", shiftTableData.ShiftCodes);
			setValue("Crews", shiftTableData.Crews);
			setValue("PatternDays", shiftTableData.PatternDays);
			setValue("CrewRotation", shiftTableData.CrewRotation);
			setValue("Comments", shiftTableData.Comments);
			setInitialShiftPerDayAndCrewsNumber(!initialShiftPerDayAndCrewsNumber);
		}
	}, [show]);

	useEffect(() => {
		setValue("ShiftsPerDay", crewRotation.length / rotationPattern);
		setValue("NumberOfCrews", crewsCodes.length);
	}, [initialShiftPerDayAndCrewsNumber]);

	return (
		<>
			<Grid container>
				<Grid item>
					<MesfModal
						title="EDIT SHIFT"
						open={show}
						handleClose={() => onHide(false)}
						id="edit-shift-modal"
						maxWidth="md"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content style={{ padding: "15px 30px 0" }}>
								<Grid container spacing={2}>
									<Controller
										name="PatternStart"
										control={control}
										render={({ field: { value, onChange } }) => (
											<MuiPickersUtilsProvider utils={MomentUtils}>
												<Grid item md={6} xs={12} style={{ margin: "8px 0 0" }}>
													<KeyboardDatePicker
														fullWidth
														disablePast
														disableToolbar
														variant="inline"
														format="MM/DD/yyyy"
														id="date-picker-inline"
														style={{
															minWidth: "100%",
															borderTopLeftRadius: 3,
															borderTopRightRadius: 3,
														}}
														label="Start"
														value={value}
														onChange={(evt: any) => onChange(evt?.toDate())}
														KeyboardButtonProps={{
															"aria-label": "change date",
														}}
													/>
												</Grid>
												<Grid
													item
													md={6}
													xs={12}
													style={{ margin: "8px 0 18px" }}
												>
													<KeyboardTimePicker
														fullWidth
														variant="inline"
														id="time-picker"
														label="Time"
														value={value}
														onChange={(evt: any) => onChange(evt?.toDate())}
														KeyboardButtonProps={{
															"aria-label": "change time",
														}}
													/>
												</Grid>
											</MuiPickersUtilsProvider>
										)}
									/>

									<Grid item md={6} xs={6}>
										<Controller
											name="ShiftsPerDay"
											control={control}
											rules={{
												min: 1,
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													label={`# ${
														shiftsPerDay > 1 ? "Shifts" : "Shift"
													}  per day`}
													variant="outlined"
													error={!!errors.ShiftsPerDay}
													fullWidth
													margin="dense"
													autoComplete="off"
													type="number"
													value={value}
													onChange={
														// FIXME: límite de 10?
														(evt) =>
															parseInt(evt.target.value) >= 1 &&
															parseInt(evt.target.value) <= 10 &&
															onChange(evt)
													}
												/>
											)}
										/>
									</Grid>
									<Grid item md={6} xs={6} style={{ paddingBottom: "0" }}>
										<Controller
											name="ShiftCodes"
											control={control}
											rules={{
												validate: (value) => value.length == shiftsPerDay,
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													label="Shifts Ids"
													variant="outlined"
													error={!!errors.ShiftCodes}
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(evt) => {
														// TODO: aquí deberíamos prevenir, que no pueda haber dos valores iguales, . Ej. dos DNN
														if (evt.target.value.length <= shiftsPerDay)
															onChange(
																evt.target.value.toString().toUpperCase(),
															);
													}}
												/>
											)}
										/>
										{!!errors.ShiftCodes && (
											<span className={classes.errorLabel}>
												Please put {shiftsPerDay} shifts
											</span>
										)}
										{shiftCodes.length < shiftsPerDay &&
											!!errors.ShiftCodes === false && (
												<span
													className={classes.infoLabel}
												>{`Insert: ${shiftsPerDay} ${
													shiftsPerDay == 1 ? "Id" : "Ids"
												}. ${
													shiftsPerDay > 1 ? "The Ids must be differents" : ""
												}`}</span>
											)}
									</Grid>
									<Grid item md={6} xs={6}>
										<Controller
											name="NumberOfCrews"
											control={control}
											rules={{
												min: 1,
												max: 9,
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													label={`# ${numberOfCrews > 1 ? "Crews" : "Crew"}`}
													variant="outlined"
													error={!!errors.NumberOfCrews}
													fullWidth
													margin="dense"
													autoComplete="off"
													type="number"
													value={value}
													onChange={(evt) => {
														if (
															parseInt(evt.target.value) >= 1 &&
															parseInt(evt.target.value) <= 9
														)
															onChange(evt);
													}}
												/>
											)}
										/>
										{errors.NumberOfCrews?.type === "min" && (
											<span className={classes.errorLabel}>
												# Crewss day is required
											</span>
										)}
									</Grid>
									<Grid item md={6} xs={6} style={{ paddingBottom: "0" }}>
										<Controller
											name="Crews"
											control={control}
											rules={{
												validate: (value) => value.length == numberOfCrews,
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													label="Crews Ids"
													variant="outlined"
													error={!!errors.Crews}
													disabled={numberOfCrews <= 0}
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(evt) => {
														if (evt.target.value.length <= numberOfCrews)
															onChange(
																evt.target.value.toString().toUpperCase(),
															);
													}}
												/>
											)}
										/>
										{!!errors.Crews && (
											<span className={classes.errorLabel}>
												Please put {numberOfCrews} crews
											</span>
										)}
										{crewsCodes.length < numberOfCrews &&
											!!errors.Crews === false && (
												<span
													className={classes.infoLabel}
												>{`Insert: ${numberOfCrews} ${
													numberOfCrews == 1 ? "Crew" : "Crews"
												}. ${
													numberOfCrews > 1
														? "The Crews must be differents"
														: ""
												}`}</span>
											)}
									</Grid>
									<Grid item md={6} xs={6}>
										<Controller
											name="PatternDays"
											control={control}
											// rules={{
											//     required: true
											// }}
											render={({ field: { value, onChange } }) => (
												<TextField
													label="Rotation Pattern"
													variant="outlined"
													error={!!errors.PatternDays}
													fullWidth
													type="number"
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={
														(evt) => {
															// FIXME: consultar si estos valores están bien, son los limites del sP
															if (
																parseInt(evt.target.value) >= 5 &&
																parseInt(evt.target.value) <= 60
															)
																onChange(evt);
														}
														//TODO: SUMAR UN HELPER: que diga: introduce a value betwen 5 and 60
													}
												/>
											)}
										/>
									</Grid>
									<Grid item md={12} xs={12} style={{ padding: "0 8px" }}>
										<Controller
											name="CrewRotation"
											control={control}
											rules={{
												validate: (value) =>
													value.length == maxCrewRotationLength,
											}}
											render={({ field: { value, onChange } }) => (
												<TextField
													value={value}
													onChange={(evt) => {
														if (
															evt.target.value.length <= maxCrewRotationLength
														)
															onChange(
																evt.target.value.toString().toUpperCase(),
															);
													}}
													error={!!errors.CrewRotation}
													label="Crew rotation"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
												/>
											)}
										/>
										{
											<Grid container style={{ padding: "0", height: 2 }}>
												<Grid
													item
													style={{
														height: 2,
														width: `${calculatePercentage()}% `,
														backgroundColor: `${
															crewRotation.length == maxCrewRotationLength
																? "#4CAF50"
																: "#F44336"
														}`,
														transition: " 0.2s ease",
														margin: "0 5px",
													}}
												></Grid>
											</Grid>
										}
										<Grid container style={{ margin: "4px 0 0" }}>
											<Grid item>
												{(crewRotation?.length as number) >= 1 &&
													(crewRotation?.length as number) !==
														maxCrewRotationLength && (
														<span
															className={classes.infoLabel}
															style={{ marginLeft: 5 }}
														>{`${crewRotation?.length}/${maxCrewRotationLength}`}</span>
													)}
											</Grid>
										</Grid>
										{!!errors.CrewRotation && (
											<span className={classes.errorLabel}>
												Please put {maxCrewRotationLength} crews
											</span>
										)}
										{numberOfCrews > 0 &&
											Number.isInteger(
												maxCrewRotationLength / numberOfCrews,
											) === false && (
												<Grid container style={{ padding: "0", marginLeft: 5 }}>
													<Grid
														item
														className={classes.errorLabel}
													>{`Rotation # not a multiple of Shifts (${maxCrewRotationLength}) / Crews (${numberOfCrews})`}</Grid>
												</Grid>
											)}
									</Grid>
									<Grid
										item
										md={12}
										xs={12}
										style={{ margin: "12px 0 20px", padding: "0 8px" }}
									>
										<Controller
											name="Comments"
											control={control}
											render={({ field: { value, onChange } }) => (
												<TextField
													style={{ margin: "8px 0" }}
													size="medium"
													maxRows={5}
													label="Comments"
													multiline
													variant="outlined"
													fullWidth
													autoComplete="off"
													value={value}
													onChange={(evt) => {
														if (evt.target.value.length <= 200) onChange(evt);
													}}
												/>
											)}
										/>
										<Grid container style={{ marginBottom: 18 }}>
											<Grid item>
												{(comments?.length as number) >= 1 &&
													(comments?.length as number) !== 200 && (
														<span
															className={classes.infoLabel}
															style={{ marginLeft: 5 }}
														>{`${comments?.length}/200`}</span>
													)}
												{(comments?.length as number) == 200 && (
													<span
														className={classes.infoLabel}
														style={{ marginLeft: 5 }}
													>
														Max. 200
													</span>
												)}
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="default"
											onClick={() => onHide(false)}
										>
											Cancel
										</Button>
									</Grid>
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
										>
											Save
										</Button>
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid>
			</Grid>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="success" onClose={handleClose}>
					The shift was created successfully
				</Alert>
			</Snackbar>
		</>
	);
};
